import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../ui/Icon';
import Reveal from '../ui/Reveal';
import PatternAccent from '../ui/PatternAccent';
import founders from '../../assets/home/founders.webp';

// The founders' own section rather than a line inside a card.
//
// H. Tatang Sujani and Hj. Siti E Rochaety started Ampera in 1963 from a
// kaki-lima at the edge of Kebon Kelapa terminal. That portrait and that
// story are the most particular thing on the whole site — everything else
// (mountains, platters, batik) could belong to any Sundanese restaurant.
// It gets room to breathe, and the caption names them, because an unnamed
// old photograph reads as stock imagery.

export default function Founders() {
  const { t } = useTranslation();

  return (
    <section id="founders" className="relative scroll-mt-20 overflow-hidden bg-[#faf3ee]">
      <PatternAccent at="top-right" className="w-64 md:w-80" opacity={0.08} />
      <div className="relative z-10 mx-auto max-w-7xl px-4 py-14 sm:px-6 md:py-20 lg:px-8">
        <div className="grid items-center gap-10 lg:grid-cols-[minmax(0,0.85fr)_1fr] lg:gap-14">
          {/* ---- portrait ---- */}
          <Reveal from="left">
            <figure className="mx-auto max-w-sm lg:max-w-none">
              {/* The mat wraps the print only. Sized to the image rather than
                  the whole figure, or it reads as a pink block under the
                  caption instead of an offset behind the photograph. */}
              <div className="relative">
                <div
                  aria-hidden="true"
                  className="absolute -bottom-3 -left-3 h-full w-full rounded-2xl bg-primary-600/10"
                />
                <img
                  src={founders}
                  alt={t('home.founders.portraitAlt')}
                  loading="lazy"
                  className="relative w-full rounded-2xl border-4 border-white object-cover shadow-xl"
                />
              </div>
              <figcaption className="mt-5 text-center">
                <div className="font-serif text-base font-semibold text-gray-900">
                  {t('home.founders.names', 'H. Tatang Sujani & Hj. Siti E Rochaety')}
                </div>
                <div className="mt-0.5 text-xs uppercase tracking-[0.16em] text-primary-600">
                  {t('home.founders.role', 'Founders')}
                </div>
              </figcaption>
            </figure>
          </Reveal>

          {/* ---- story ---- */}
          <Reveal from="right">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary-600">
              {t('home.founders.eyebrow')}
            </span>
            <h2 className="mt-3 font-serif text-3xl font-bold leading-tight text-gray-900 md:text-4xl">
              {t('home.founders.title')}
            </h2>

            <p className="mt-5 leading-relaxed text-gray-600">{t('home.founders.paragraph1')}</p>
            <p className="mt-3.5 leading-relaxed text-gray-600">{t('home.founders.paragraph2')}</p>

            <div className="mt-7 flex flex-wrap items-center gap-x-7 gap-y-3 border-t border-primary-200/60 pt-5">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Icon name="sprout" className="h-4 w-4 text-primary-500" />
                {t('home.founders.origin', 'It began with a single street cart')}
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Icon name="mapPin" className="h-4 w-4 text-primary-500" />
                {t('home.founders.place', 'Kebon Kelapa terminal, Bandung')}
              </div>
            </div>

            <Link
              to="/brand"
              className="group mt-6 inline-flex items-center gap-2 font-semibold text-primary-600 transition-colors hover:text-primary-700"
            >
              {t('home.founders.cta')}
              <Icon name="arrowRight" className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
